import type { ActionUpdateResult } from '../../core/api/types';
import { reachState } from '../../core/reach/reach.store';
import { account } from '../../core/session/session.store';
import { isUnreadableRow, type OutboxRecord } from '../../core/outbox/device-database';
import { submitChange, unsentRows, withdrawChange } from '../../core/outbox/outbox.store';
import { submit, type OptimisticHost, type RereadPlan, type WriteOutcome } from './optimistic';

/**
 * 036 (issue 240, T092) — the write shape `setCompleted`, `toggleStar` and `defer` share across the
 * four feature stores that render an action row: complete/reopen, star/unstar and defer/show-now,
 * each routed through the outbox so ticking, starring and deferring work with no connection (issue
 * 209's own goal — "a tick is drawn at once, where it stands, marked as waiting").
 *
 * **The optimistic step is always the identity, and that is deliberate — not an oversight.**
 * Constitution II / issue 209's rule: a row shows the practitioner's own change and nothing that follows
 * from it — no row added, removed or moved, no count changed, no successor, no newly available
 * action — until the change is actually confirmed. `action-row.tsx` draws the change itself, read
 * from `useUnsentFor` (T091); the underlying list here is touched only by `settle`/`reread`, and
 * only once a real answer has arrived.
 *
 * This is also how 4b's second review finding is closed ("a dead line that still reads reachable"):
 * a line that CLAIMS `reachable` can still turn out to be dead, and that is only discovered once the
 * request is actually tried — so the client cannot safely remove a row ahead of an answer it has not
 * received, whether or not `reachState()` currently says `reachable`. Special-casing "reachable but
 * stalled" would need a way to tell it apart from "reachable and about to answer quickly", which
 * does not exist; removing the optimistic transform entirely removes the need for that distinction —
 * the row never moves until the server has actually spoken, in every case, online included. Online,
 * this costs one round trip's worth of extra wait before the row visibly leaves the list (it used to
 * vanish before the request was even sent); SC-020 is what measures whether that is discernible.
 *
 * **A second tap on the SAME field withdraws the queued change instead of queuing another one, and
 * that is how 4b's first review finding is closed.** `submit()` only releases a row's `pending` mark
 * once its `dispatch()` settles, which can take up to `SEND_TIMEOUT_MS` against a stalled connection
 * — so a second tap routed through `submit()` again would sit blocked for that long (T091's own
 * scenario: "ticking a row that carries a waiting tick withdraws it"). This never reaches `submit()`
 * at all when a matching change is already queued: it calls `withdrawChange` directly, which
 * resolves in one device transaction — and, because `withdrawChange` also SETTLES the outbox's own
 * waiter for that row (`core/outbox/outbox.store.ts`'s own comment on `withdrawChange`), is exactly
 * what unblocks the FIRST call's still-pending `dispatch()` promise, releasing ITS `pending` mark at
 * the same moment. No change to `submit()` or `submitChange()` themselves, and no change to any of
 * 4b's own specs.
 *
 * The match is by FIELD, not by the value being requested: `setCompleted`, `toggleStar` and `defer`
 * all compute their target from the row's own SERVER-known value (`!action.completedAt`,
 * `!action.isStarred`), which does not change while a change is still unsent — so a second tap
 * always asks for the same thing the first one did, and "the same field is already queued" is what
 * tells a genuine repeat from a first request apart.
 *
 * **036 (issue 257, A2) — a repeat for a DIFFERENT value withdraws the old row and queues the new
 * one; only a repeat for the SAME value is a pure withdraw.** Before this fix, finding a waiting
 * change on the same field always withdrew it and returned — full stop, whatever the new request
 * asked for. That is correct for "a tick then an un-tick" (the second request asks for the same
 * value `!action.completedAt` always computes, so withdrawing IS queuing nothing — there is nothing
 * left to want). It is wrong for `defer`: offline, defer to Monday, then defer to Friday, and the
 * second call found Monday already queued, withdrew it, and queued nothing — this function's own
 * header comment used to call that "the one unambiguous reading", which was simply incorrect: the
 * practitioner asked for Friday twice as hard as they asked for Monday once. Comparing the two
 * REQUEST BODIES (not merely "a change on this field exists") is what tells the two cases apart.
 */
export async function submitOrWithdraw<TData, TRereadResult>(
  host: OptimisticHost<TData>,
  actionId: number,
  field: string,
  body: Readonly<Record<string, unknown>>,
  shows: Readonly<Record<string, unknown>>,
  settle: (data: TData, result: ActionUpdateResult) => TData,
  reread: RereadPlan<TData, ActionUpdateResult, TRereadResult>,
): Promise<WriteOutcome> {
  const waiting = findWaitingChange(actionId, field);
  if (waiting) {
    const practitionerId = account.get()?.practitionerId ?? null;
    const sameValue = sameRequestBody(waiting.request.body, body);
    const outcome = await withdrawChange(waiting.seq, practitionerId);
    // `refused` only ever means the row was already sending (a moment from resolving on its own) or
    // already gone (nothing left to withdraw) — neither is a failure this caller should report as
    // one, so `not-attempted` (the guard's own "nothing happened" answer) rather than `refused`.
    if (outcome.kind !== 'ok') return 'not-attempted';
    // A tick then an un-tick (and any other repeat asking for the SAME value): the withdraw above is
    // the whole effect — nothing new to queue.
    if (sameValue) return 'written';
    // A2: a DIFFERENT value — Monday withdrawn, Friday queued fresh, exactly like a first request.
  }

  return submit(
    host,
    actionId,
    (data) => data,
    (data) => data,
    () => reachState() === 'reachable',
    () => submitChange<ActionUpdateResult>({ actionId, body, shows }),
    settle,
    reread,
  );
}

/** Content equality for one outbox row's own `request.body` against a freshly-requested body — the
 *  comparison A2 needs to tell "the same value, again" from "a different value, since" apart. Both
 *  sides are small, JSON-safe records (never functions or other non-plain values) built by this same
 *  module's own three callers, so this is correct and simple rather than a hand-rolled field walk. */
function sameRequestBody(stored: unknown, requested: Readonly<Record<string, unknown>>): boolean {
  return JSON.stringify(stored) === JSON.stringify(requested);
}

/** The still-`waiting` change row for one action's field, if any — the one definition of "a change
 *  to this field is already queued", shared with `replace-waiting-field.ts` (issue 259's editors). */
export function findWaitingChange(actionId: number, field: string): OutboxRecord | undefined {
  return unsentRows.get().find(
    (row): row is OutboxRecord =>
      !isUnreadableRow(row)
      && row.kind === 'change'
      && row.actionId === actionId
      && row.state === 'waiting'
      && field in (row.shows ?? {}),
  );
}
